// Nomad-mon Go! — FRONT DOOR 6 "spell" layer for the medallion opening.
// Each effect here is one GPU-cheap primitive driven by src/entry.js:
//   lightning   branching bolts as screen-plane ribbons (one draw call per pool)
//   plasma      an energy-fire corona that ignites around the medal silhouette
//   sparks      analytic instanced sparks (no CPU simulation)
//   caustic     spectral caustic light that sweeps the relief (replaces the sheen)
//   lens        one post pass: shockwave refraction with chromatic dispersion,
//               heat shimmer around the rim, a spectral zoom into the light,
//               and the bloom flash. Enabled only while the opening is playing.
// All randomness is presentation-only (own mulberry32), never the game rng.

import * as THREE from 'three';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

// ------------------------------------------------------------------ lightning
function jag(rng, a, b, levels, rough) {
  let pts = [a, b];
  let disp = Math.hypot(b[0] - a[0], b[1] - a[1]) * rough;
  for (let l = 0; l < levels; l++) {
    const next = [pts[0]];
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i], q = pts[i + 1];
      const dx = q[0] - p[0], dy = q[1] - p[1], len = Math.hypot(dx, dy) || 1;
      const off = (rng() * 2 - 1) * disp;
      next.push([(p[0] + q[0]) / 2 - dy / len * off, (p[1] + q[1]) / 2 + dx / len * off], q);
    }
    pts = next;
    disp *= 0.5;
  }
  return pts;
}

// one bolt = a jagged main channel plus a few forked branches that thin out
export function boltLines(rng, a, b, { levels = 5, rough = 0.2, branches = 3, width = 1, inten = 1,
  fadeIn = 0.12, taperEnd = 0.6 } = {}) {
  const main = jag(rng, a, b, levels, rough);
  const lines = [{ pts: main, width, inten, fadeIn, taperEnd }];
  const mainLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
  for (let k = 0; k < branches; k++) {
    const i = Math.floor((0.18 + rng() * 0.55) * (main.length - 2)) + 1;
    const p = main[i], q = main[i + 1];
    const base = Math.atan2(q[1] - p[1], q[0] - p[0]);
    const ang = base + (rng() < 0.5 ? -1 : 1) * (0.35 + rng() * 0.5);
    const len = mainLen * (0.14 + rng() * 0.26);
    const end = [p[0] + Math.cos(ang) * len, p[1] + Math.sin(ang) * len];
    lines.push({ pts: jag(rng, p, end, Math.max(2, levels - 1), rough * 1.15),
      width: width * 0.55, inten: inten * 0.6, fadeIn: 0, taperEnd: 0.05 });
  }
  return lines;
}

const BOLT_VERT = /* glsl */`
  attribute vec2 aBoltTan;
  attribute float aBoltSide;
  attribute float aBoltWidth;
  attribute float aBoltSlot;
  attribute float aBoltInt;
  uniform float uBright[12]; uniform float uWidth;
  varying float vSide, vI;
  void main(){
    vec2 n = vec2(-aBoltTan.y, aBoltTan.x);
    vec3 p = position + vec3(n * aBoltSide * aBoltWidth * uWidth, 0.0);
    vSide = aBoltSide; vI = aBoltInt * uBright[int(aBoltSlot + 0.5)];
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;
const BOLT_FRAG = /* glsl */`
  precision highp float;
  varying float vSide, vI;
  uniform vec3 uCore, uGlow; uniform float uFade;
  void main(){
    if (vI < 0.002) discard;
    float x = abs(vSide);
    float core = exp(-x * x * 70.0);
    float glow = exp(-x * x * 7.0) * 0.55 + exp(-x * 3.2) * 0.25;
    vec3 c = uCore * core + uGlow * glow;
    gl_FragColor = vec4(c * vI * uFade, 1.0);
  }
`;

export function makeLightning({ slots = 10, segCap = 100, width = 0.06, z = 0, renderOrder = 6,
  core = [7.5, 7.2, 8.2], glow = [0.42, 0.52, 1.7] } = {}) {
  // non-indexed triangle list: 6 vertices per segment, unused segments are
  // zero-width (degenerate) so they rasterize nothing
  const per = segCap * 6, V = slots * per;
  const pos = new Float32Array(V * 3), tan = new Float32Array(V * 2), side = new Float32Array(V);
  const w = new Float32Array(V), slotA = new Float32Array(V), inten = new Float32Array(V);
  for (let s = 0; s < slots; s++) slotA.fill(s, s * per, (s + 1) * per);
  const geo = new THREE.BufferGeometry();
  const attr = {
    position: new THREE.BufferAttribute(pos, 3), aBoltTan: new THREE.BufferAttribute(tan, 2),
    aBoltSide: new THREE.BufferAttribute(side, 1), aBoltWidth: new THREE.BufferAttribute(w, 1),
    aBoltSlot: new THREE.BufferAttribute(slotA, 1), aBoltInt: new THREE.BufferAttribute(inten, 1),
  };
  for (const [k, a] of Object.entries(attr)) { a.setUsage(THREE.DynamicDrawUsage); geo.setAttribute(k, a); }
  const uniforms = {
    uBright: { value: new Array(12).fill(0) }, uWidth: { value: width }, uFade: { value: 1 },
    uCore: { value: new THREE.Vector3(...core) }, uGlow: { value: new THREE.Vector3(...glow) },
  };
  const mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({
    uniforms, vertexShader: BOLT_VERT, fragmentShader: BOLT_FRAG,
    transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    // the face arcs ride the medal through its turn, so its back must draw too;
    // additive, so one pass is enough
    side: THREE.DoubleSide, forceSinglePass: true,
  }));
  mesh.frustumCulled = false;
  mesh.renderOrder = renderOrder;
  mesh.position.z = z;

  function write(s, lines) {
    let v = s * per;
    const vEnd = v + per;
    const put = (p, tx, ty, sgn, width, I) => {
      pos[v * 3] = p[0]; pos[v * 3 + 1] = p[1]; pos[v * 3 + 2] = 0;
      tan[v * 2] = tx; tan[v * 2 + 1] = ty; side[v] = sgn; w[v] = width; inten[v] = I;
      v++;
    };
    for (const line of lines) {
      const n = line.pts.length;
      if (v + (n - 1) * 6 > vEnd) break;
      const T = [], W = [], I = [];
      for (let i = 0; i < n; i++) {
        const a = line.pts[Math.max(0, i - 1)], b = line.pts[Math.min(n - 1, i + 1)];
        let tx = b[0] - a[0], ty = b[1] - a[1];
        const tl = Math.hypot(tx, ty) || 1;
        T.push([tx / tl, ty / tl]);
        const t = i / (n - 1);
        W.push(line.width * (1 + (line.taperEnd - 1) * t));
        I.push(line.inten * (line.fadeIn > 0 ? Math.min(1, t / line.fadeIn) : 1));
      }
      for (let i = 0; i < n - 1; i++) {
        const p = line.pts[i], q = line.pts[i + 1], j = i + 1;
        // counter-clockwise in the ribbon's plane (front-facing)
        put(p, T[i][0], T[i][1], 1, W[i], I[i]); put(p, T[i][0], T[i][1], -1, W[i], I[i]);
        put(q, T[j][0], T[j][1], -1, W[j], I[j]);
        put(p, T[i][0], T[i][1], 1, W[i], I[i]); put(q, T[j][0], T[j][1], -1, W[j], I[j]);
        put(q, T[j][0], T[j][1], 1, W[j], I[j]);
      }
    }
    w.fill(0, v, vEnd); inten.fill(0, v, vEnd);
    for (const a of Object.values(attr)) a.needsUpdate = true;
  }

  // slot lifecycle: strokes = re-strike flashes along the same channel;
  // regen = an arc that re-forms its path while it lives (crawling arcs)
  const live = new Array(slots).fill(null);
  function spawn(now, gen, { life = 0.4, strokes = null, regen = 0, inten = 1 } = {}) {
    let s = live.findIndex((x) => !x);
    if (s < 0) {                                   // steal the oldest
      let oldest = 0;
      for (let i = 1; i < slots; i++) if (live[i].start < live[oldest].start) oldest = i;
      s = oldest;
    }
    live[s] = { start: now, life, strokes, regen, inten, gen, lastGen: now };
    write(s, gen());
    return s;
  }
  function update(now, flick) {
    const b = uniforms.uBright.value;
    let count = 0;
    for (let s = 0; s < slots; s++) {
      const L = live[s];
      if (!L) { b[s] = 0; continue; }
      const age = now - L.start;
      if (age > L.life || age < -0.05) { live[s] = null; b[s] = 0; continue; }
      count++;
      if (L.regen && now - L.lastGen >= L.regen) { L.lastGen = now; write(s, L.gen()); }
      let v;
      if (L.strokes) {
        v = 0.16 * Math.exp(-age / 0.16);
        for (const [ts, amp] of L.strokes) if (age >= ts) v = Math.max(v, amp * Math.exp(-(age - ts) / 0.034));
      } else {
        v = Math.min(1, age / 0.03) * Math.min(1, (L.life - age) / 0.07) * (0.62 + 0.38 * flick());
      }
      b[s] = v * L.inten;
    }
    return count;
  }
  function clear() { live.fill(null); uniforms.uBright.value.fill(0); }
  return { mesh, uniforms, spawn, update, clear, get active() { return live.some(Boolean); } };
}

// ------------------------------------------------------------------ plasma rim
// Energy fire licking outward from the medal silhouette. The silhouette is an
// ellipse while the medal turns (uSquash = |cos yaw|), so the flames hug the
// coin even edge-on. Temperature ramp: white-hot -> ice -> electric violet.
export function makePlasma(octaves = 4) {
  const uniforms = { uTime: { value: 0 }, uAmt: { value: 0 }, uSquash: { value: 1 }, uFade: { value: 1 } };
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(4.0, 4.0), new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    defines: { OCT: octaves },
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv;
      uniform float uTime, uAmt, uSquash, uFade;
      float h3(vec3 p){ p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
      float n3(vec3 x){ vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(mix(h3(i), h3(i + vec3(1,0,0)), f.x), mix(h3(i + vec3(0,1,0)), h3(i + vec3(1,1,0)), f.x), f.y),
                   mix(mix(h3(i + vec3(0,0,1)), h3(i + vec3(1,0,1)), f.x), mix(h3(i + vec3(0,1,1)), h3(i + vec3(1,1,1)), f.x), f.y), f.z); }
      float fbm(vec3 p){ float a = 0.5, s = 0.0; for (int i = 0; i < OCT; i++){ s += a * n3(p); p = p * 2.03 + vec3(1.7, 9.2, 3.1); a *= 0.5; } return s; }
      void main(){
        if (uAmt < 0.003) discard;
        vec2 p = (vUv - 0.5) * 4.0;
        vec2 q = vec2(p.x / max(uSquash, 0.07), p.y);
        float r = length(q);
        float d = r - 0.80;
        float reach = 0.10 + 0.55 * uAmt;
        if (d < -0.06 || d > reach + 0.1) discard;
        vec2 dir = q / max(r, 1e-4);
        // flames rise outward: noise scrolls along the radial axis
        vec3 np = vec3(dir * 3.2, d * 5.5 - uTime * 2.4);
        float warp = fbm(np * 0.7 + vec3(0.0, 0.0, uTime * 0.6));
        float f = fbm(np + vec3(warp * 1.6, warp * 1.1, 0.0));
        float body = f * 1.3 - d / reach;                 // flame height field
        // defined tongues, not a haze: a narrow transition band at the flame edge
        float flame = smoothstep(0.30, 0.48, body) * smoothstep(-0.05, 0.0, d);
        float hot = smoothstep(0.62, 1.0, body) * exp(-max(d, 0.0) * 9.0);
        vec3 violet = vec3(0.40, 0.20, 1.0), ice = vec3(0.45, 0.72, 1.0), white = vec3(1.0, 0.97, 1.0);
        vec3 col = mix(violet, ice, smoothstep(0.42, 0.8, body));
        col = mix(col, white, hot);
        // the hottest layer sits right on the rim, a thin incandescent line
        float rim = exp(-pow(d / 0.012, 2.0)) * (0.5 + 0.5 * f);
        vec3 c = col * flame * (0.7 + 1.2 * hot) + white * rim * 0.7;
        gl_FragColor = vec4(c * uAmt * uFade, 1.0);
      }
    `,
  }));
  mesh.position.z = 0.09;
  mesh.renderOrder = 5;
  return { mesh, uniforms };
}

// ------------------------------------------------------------------ sparks
// Burst 0 = the strike (three impact sites), burst 1 = the climax ring.
export function makeSparks(rng, count, sites) {
  const base = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = base.index;
  geo.setAttribute('position', base.getAttribute('position'));
  const A = new Float32Array(count * 4), B = new Float32Array(count * 4);
  const strike = Math.round(count * 0.4);
  for (let i = 0; i < count; i++) {
    const first = i < strike;
    const site = first ? sites[i % sites.length] + (rng() - 0.5) * 0.12 : rng() * Math.PI * 2;
    A[i * 4] = site;
    A[i * 4 + 1] = (rng() - 0.5) * (first ? 2.0 : 0.7);
    A[i * 4 + 2] = first ? 1.4 + rng() * 2.6 : 2.0 + rng() * 3.6;
    A[i * 4 + 3] = 2.2 + rng() * 2.4;
    B[i * 4] = first ? rng() * 0.06 : rng() * 0.08;
    B[i * 4 + 1] = 0.35 + rng() * (first ? 0.5 : 0.85);
    B[i * 4 + 2] = 0.014 + rng() * 0.022;
    B[i * 4 + 3] = first ? 0 : 1;
  }
  geo.setAttribute('aA', new THREE.InstancedBufferAttribute(A, 4));
  geo.setAttribute('aB', new THREE.InstancedBufferAttribute(B, 4));
  geo.instanceCount = count;
  const uniforms = { uT: { value: -10 }, uBirth0: { value: 99 }, uBirth1: { value: 99 }, uR: { value: 0.8 },
    uGrav: { value: 1.6 }, uFade: { value: 1 } };
  const mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      attribute vec4 aA, aB; uniform float uT, uBirth0, uBirth1, uR, uGrav;
      varying vec2 vUv; varying float vHeat;
      void main(){
        float birth = aB.w < 0.5 ? uBirth0 : uBirth1;
        float age = uT - birth - aB.x;
        if (age < 0.0 || age > aB.y) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vHeat = 0.0; vUv = vec2(0.0); return; }
        vec2 n = vec2(cos(aA.x), sin(aA.x));
        float da = aA.x + aA.y; vec2 dir = vec2(cos(da), sin(da));
        float k = aA.w, e = exp(-k * age);
        vec2 p = n * uR + dir * aA.z * (1.0 - e) / k + vec2(0.0, -0.5 * uGrav * age * age);
        vec2 v = dir * aA.z * e + vec2(0.0, -uGrav * age);
        float sp = length(v); vec2 ax = v / max(sp, 1e-4); vec2 ay = vec2(-ax.y, ax.x);
        float L = aB.z * 1.6 + sp * 0.05, W = aB.z;
        vec2 q = p + ax * position.x * L + ay * position.y * W;
        vHeat = 1.0 - age / aB.y; vUv = position.xy * 2.0;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(q, 0.0, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv; varying float vHeat; uniform float uFade;
      void main(){
        float a = exp(-(vUv.x * vUv.x * 2.2 + vUv.y * vUv.y * 5.0));
        float h = vHeat * vHeat;
        vec3 c = mix(vec3(0.45, 0.30, 1.0), mix(vec3(0.6, 0.82, 1.0), vec3(1.0, 0.98, 0.95), h), vHeat);
        gl_FragColor = vec4(c * a * (0.6 + 5.0 * h) * uFade, 1.0);
      }
    `,
  }));
  mesh.frustumCulled = false;
  mesh.renderOrder = 7;
  mesh.position.z = 0.1;
  return { mesh, uniforms, count };
}

// ------------------------------------------------------------------ caustic
// Spectral caustic light sweeping the relief: a refracted-light network
// evaluated three times at slightly different scales, so its edges split into
// a soft spectrum (dispersion), modulated by the crest's own relief slope.
export function makeCaustic(radius, z) {
  const uniforms = { uTime: { value: 0 }, uFade: { value: 1 }, uFlare: { value: 0 }, uAmt: { value: 0.32 },
    uSweep: { value: 0 }, tBump: { value: null }, uHasBump: { value: 0 } };
  const mesh = new THREE.Mesh(new THREE.CircleGeometry(radius, 72), new THREE.ShaderMaterial({
    uniforms, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: `varying vec2 vUv; void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv;
      uniform float uTime, uFade, uFlare, uAmt, uSweep, uHasBump; uniform sampler2D tBump;
      float caustic(vec2 uv, float t){
        vec2 p = uv * 6.2831 - 250.0, i = p; float c = 1.0;
        for (int n = 0; n < 4; n++){
          float tt = t * (1.0 - 3.5 / float(n + 1));
          i = p + vec2(cos(tt - i.x) + sin(tt + i.y), sin(tt - i.y) + cos(tt + i.x));
          c += 1.0 / length(vec2(p.x / (sin(i.x + tt) / 0.005), p.y / (cos(i.y + tt) / 0.005)));
        }
        c /= 4.0; c = 1.17 - pow(c, 1.4);
        return pow(abs(c), 7.0);
      }
      void main(){
        vec2 p = vUv - 0.5;
        float edge = smoothstep(0.5, 0.42, length(p));
        float t = uTime * 0.35;
        vec2 cu = p * 1.35 + vec2(0.31, 0.17);
        vec3 c = vec3(caustic(cu * 0.985, t), caustic(cu, t), caustic(cu * 1.015, t));
        // the light arrives as a slow diagonal band crossing the face
        float band = exp(-pow((p.x * 0.8 + p.y * 0.6) - uSweep, 2.0) * 9.0);
        float relief = 1.0;
        if (uHasBump > 0.5) {
          float h = texture2D(tBump, vUv).r;
          float hx = texture2D(tBump, vUv + vec2(0.006, 0.0)).r - h;
          float hy = texture2D(tBump, vUv + vec2(0.0, 0.006)).r - h;
          relief = 0.35 + 9.0 * length(vec2(hx, hy)) + 0.5 * h;
        }
        vec3 col = c * vec3(0.85, 0.9, 1.15) * band * relief * uAmt;
        col += vec3(1.0, 0.97, 0.93) * uFlare * 0.5;
        float a = edge * uFade;
        gl_FragColor = vec4(col * a, a * max(max(col.r, col.g), col.b));
      }
    `,
  }));
  mesh.position.z = z;
  return { mesh, uniforms };
}

// ------------------------------------------------------------------ lens pass
export function makeLensPass(taps = 10) {
  const pass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null }, uCenter: { value: new THREE.Vector2(0.5, 0.6) }, uAspect: { value: 0.46 },
      uR: { value: 0.15 }, uShock0: { value: -1 }, uShock1: { value: -1 }, uHeat: { value: 0 },
      uZoom: { value: 0 }, uFlash: { value: 0 }, uTime: { value: 0 },
      uFlashCol: { value: new THREE.Vector3(0.9, 0.9, 1.0) },
    },
    defines: { TAPS: taps },
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: /* glsl */`
      precision highp float; varying vec2 vUv;
      uniform sampler2D tDiffuse; uniform vec2 uCenter;
      uniform float uAspect, uR, uShock0, uShock1, uHeat, uZoom, uFlash, uTime;
      uniform vec3 uFlashCol;
      float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
      float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y); }
      vec3 spectral(float x){
        return clamp(vec3(exp(-pow((x - 0.82) / 0.2, 2.0)) + 0.3 * exp(-pow((x - 0.05) / 0.1, 2.0)),
                          exp(-pow((x - 0.5) / 0.2, 2.0)), exp(-pow((x - 0.16) / 0.2, 2.0))), 0.0, 1.0);
      }
      // derivative-of-gaussian ring: pushes pixels outward on its leading edge
      float shock(float r, float T, float R){
        if (T < 0.0 || T >= 1.0) return 0.0;
        float rs = R * (1.0 + T * 4.2), w = R * (0.10 + 0.25 * T);
        float x = (r - rs) / w;
        return -x * exp(-x * x) * pow(1.0 - T, 1.6) * R * 0.32;
      }
      void main(){
        vec2 d = vUv - uCenter; d.x *= uAspect;
        float r = length(d); vec2 n = d / max(r, 1e-5);
        float s = shock(r, uShock0, uR) + shock(r, uShock1, uR * 1.08);
        vec2 off = n * s;
        if (uHeat > 0.001) {
          float m = smoothstep(uR * 0.86, uR * 1.02, r) * smoothstep(uR * 2.1, uR * 1.15, r);
          vec2 hp = d / uR * 7.0 + vec2(0.0, -uTime * 2.6);
          off += (vec2(noise(hp), noise(hp + 17.3)) - 0.5) * uHeat * m * uR * 0.09;
        }
        off.x /= uAspect;
        vec3 col;
        if (uZoom > 0.001) {
          vec3 acc = vec3(0.0), wsum = vec3(0.0);
          for (int i = 0; i < TAPS; i++) {
            float t = float(i) / float(TAPS - 1);
            vec3 wgt = spectral(t) + 0.6;          // mostly white streaks, a soft spectral edge
            vec2 uv = uCenter + (vUv + off - uCenter) * (1.0 - uZoom * 0.11 * t);
            acc += texture2D(tDiffuse, uv).rgb * wgt; wsum += wgt;
          }
          col = acc / wsum;
        } else {
          // dispersion: red refracts least, violet most
          col = vec3(texture2D(tDiffuse, vUv + off * 0.9).r, texture2D(tDiffuse, vUv + off).g,
                     texture2D(tDiffuse, vUv + off * 1.1).b);
        }
        float fl = uFlash * (0.12 + 0.88 * exp(-r * r / (uR * uR * 3.5)));
        col += uFlashCol * fl * 2.2;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  pass.enabled = false;
  return pass;
}
